import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { login } from "../api/auth";
import { useAuth } from "../utils/useAuth";
import { colors, spacing, borderRadius, typography, shadows, createButtonStyle } from "../styles/theme";

// Prosta walidacja emaila
const validateEmail = (email: string) => /\S+@\S+\.\S+/.test(email);

const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const { setUser } = useAuth();
  const navigate = useNavigate();

  // Czy dane wejściowe są poprawne
  const isValid = validateEmail(email) && password.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!validateEmail(email)) {
      setError("Podaj poprawny adres email.");
      return;
    }
    if (!password) {
      setError("Podaj hasło.");
      return;
    }
    try {
      const data = await login(email, password);
      if (!data.user) {
        setError("Brak danych użytkownika w odpowiedzi serwera.");
        return;
      }
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("user", JSON.stringify(data.user));
      setUser(data.user);
      if (!data.user.is_active) {
        navigate("/waiting-activation");
      } else if (data.user.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/");
      }
    } catch (e: any) {
      if (e.response) {
        if (e.response.status === 401) {
          setError("Błędny email lub hasło.");
        } else if (e.response.status === 403) {
          setError("Konto nieaktywne lub zostało zablokowane. Skontaktuj się z administratorem.");
        } else {
          setError("Nieznany błąd logowania.");
        }
      } else {
        setError("Brak połączenia z serwerem.");
      }
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: `linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)`,
      padding: spacing.xl,
    }}>
      <div style={{
        maxWidth: 420,
        width: "100%",
        background: colors.background,
        borderRadius: borderRadius.lg,
        boxShadow: shadows.xl,
        padding: spacing.xxxl,
      }}>
        {/* Logo SEMTOP */}
        <div style={{
          textAlign: "center",
          marginBottom: spacing.xl,
        }}>
          <img
            src="/SEMTOP_Agencja_Marketingowa_Katowice.webp"
            alt="SEMTOP Logo"
            style={{
              width: 200,
              height: "auto",
              marginBottom: spacing.md,
            }}
          />
          <h1 style={{
            fontSize: typography.fontSize.xxxl,
            fontWeight: typography.fontWeight.bold,
            color: colors.textPrimary,
            marginBottom: spacing.xs,
          }}>
            Semtop Faktury
          </h1>
          <p style={{
            fontSize: typography.fontSize.sm,
            color: colors.textSecondary,
          }}>
            Zaloguj się do swojego konta
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Email */}
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{
              display: "block",
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.semibold,
              color: colors.textPrimary,
              marginBottom: spacing.xs,
            }}>
              Email
            </label>
            <input
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="twoj@email.com"
              type="email"
              required
              style={{
                width: "100%",
                padding: spacing.md,
                fontSize: typography.fontSize.base,
                border: `1px solid ${colors.border}`,
                borderRadius: borderRadius.md,
                outline: "none",
                transition: "all 0.2s ease",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = colors.primary;
                e.target.style.boxShadow = `0 0 0 3px ${colors.primaryLight}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = colors.border;
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: spacing.xl }}>
            <label style={{
              display: "block",
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.semibold,
              color: colors.textPrimary,
              marginBottom: spacing.xs,
            }}>
              Hasło
            </label>
            <input
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              type="password"
              required
              style={{
                width: "100%",
                padding: spacing.md,
                fontSize: typography.fontSize.base,
                border: `1px solid ${colors.border}`,
                borderRadius: borderRadius.md,
                outline: "none",
                transition: "all 0.2s ease",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = colors.primary;
                e.target.style.boxShadow = `0 0 0 3px ${colors.primaryLight}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = colors.border;
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!isValid}
            style={{
              ...createButtonStyle('primary', !isValid),
              width: "100%",
              padding: `${spacing.md}px ${spacing.lg}px`,
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
            }}
          >
            Zaloguj się
          </button>

          {/* Error Message */}
          {error && (
            <div style={{
              marginTop: spacing.lg,
              padding: spacing.md,
              background: colors.errorLight,
              color: colors.error,
              border: `1px solid ${colors.error}`,
              borderRadius: borderRadius.md,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.semibold,
            }}>
              ⚠️ {error}
            </div>
          )}

          {/* Register Link */}
          <div style={{
            marginTop: spacing.lg,
            textAlign: "center",
            fontSize: typography.fontSize.sm,
            color: colors.textSecondary,
          }}>
            Nie masz konta?{" "}
            <Link
              to="/register"
              style={{
                color: colors.primary,
                fontWeight: typography.fontWeight.semibold,
                textDecoration: "none",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
            >
              Zarejestruj się
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;
